import { isNil } from 'lodash'
import { createContext, useContext, useMemo } from 'react'
import { apolloClient } from '../apollo-client'
import {
  useGetPaginatedNotificationsQuery,
  usePushCommentNotificationSubscription,
} from '../generated/apollo-operations'
import { SCROLL_TRIGGER_BUFFER } from '../utils/scrollable'
import { Notification } from './types'
import { useGlobalContext } from './useGlobalContext'

export type NotificationInfo = {
  notifications: Notification[]
  totalCount: number
  totalUnreadCount: number
  userHasUnreadNotifications: boolean
  loading: boolean
  handleScroll?: (scrollRef: HTMLDivElement) => void
}

const NotificationsContext = createContext<NotificationInfo>({
  notifications: [],
  totalCount: 0,
  totalUnreadCount: 0,
  userHasUnreadNotifications: false,
  loading: false,
})

/**
 * AuthProvider is a wrapper component that provides the user's authentication information to the rest of the app.
 */
export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useGlobalContext()
  const { data, loading, fetchMore } = useGetPaginatedNotificationsQuery({
    variables: {
      input: {
        isArchived: false,
        pagination: {
          limit: 10,
          offset: 0,
        },
      },
    },
    skip: !user,
  })

  const notifications = useMemo(() => {
    return data?.paginatedNotifications.notifications || []
  }, [data])

  const totalCount = useMemo(() => {
    return data?.paginatedNotifications.totalCount || 0
  }, [data])

  const totalUnreadCount = useMemo(() => {
    return data?.paginatedNotifications.totalUnreadCount || 0
  }, [data])

  const userHasUnreadNotifications = useMemo(() => {
    return totalUnreadCount > 0
  }, [totalUnreadCount])

  // Listen for new comment notifications
  usePushCommentNotificationSubscription({
    skip: isNil(user),
    onData: ({ data }) => {
      if (!data.data) {
        return
      }
      const notification = data.data.pushCommentNotification
      if (!notification.isArchived) {
        apolloClient.cache.evict({ fieldName: 'paginatedNotifications' })
        apolloClient.cache.gc()
      }
    },
  })

  const handleScroll = (scrollRef?: HTMLDivElement) => {
    if (!scrollRef) {
      return
    }
    const { scrollTop, scrollHeight, clientHeight } = scrollRef

    // Check if the user has scrolled past the threshold to trigger fetching more data
    // Scroll buffer (200px default) is used to trigger fetching more data before the user reaches the bottom
    const isScrolledPastBuffer = scrollTop + clientHeight >= scrollHeight - SCROLL_TRIGGER_BUFFER

    if (isScrolledPastBuffer && data?.paginatedNotifications.hasNext && !loading) {
      fetchMore({
        variables: {
          input: {
            isArchived: false,
            pagination: {
              limit: 10,
              offset: notifications.length,
            },
          },
        },
      })
    }
  }

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        totalCount,
        totalUnreadCount,
        userHasUnreadNotifications,
        handleScroll,
        loading,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  )
}

export function useNotifications() {
  return useContext(NotificationsContext)
}
